
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function CalculatorSection() {
  const [monthlyRent, setMonthlyRent] = useState('3000');
  const [monthlyPayment, setMonthlyPayment] = useState('2400');
  const [dscr, setDSCR] = useState('1.25');
  const [status, setStatus] = useState('Good');

  const calculateDSCR = () => {
    const rent = parseFloat(monthlyRent) || 0;
    const payment = parseFloat(monthlyPayment) || 0;
    
    if (payment === 0) {
      setDSCR('0.00');
      setStatus('Invalid');
      return;
    }

    const ratio = rent / payment;
    setDSCR(ratio.toFixed(2));

    if (ratio >= 1.25) {
      setStatus('Excellent');
    } else if (ratio >= 1.0) {
      setStatus('Good');
    } else if (ratio >= 0.75) {
      setStatus('Fair');
    } else {
      setStatus('Poor');
    }
  };

  const openFullCalculator = () => {
    window.open('https://dscr-loan-calculator.lovable.app', '_blank');
  };

  const getStatusColor = () => {
    const ratio = parseFloat(dscr);
    if (ratio >= 1.25) return 'text-emerald-600';
    if (ratio >= 1.0) return 'text-blue-600';
    if (ratio >= 0.75) return 'text-amber-600';
    return 'text-red-600';
  };

  const getStatusBgColor = () => {
    const ratio = parseFloat(dscr);
    if (ratio >= 1.25) return 'bg-emerald-50 border-emerald-200';
    if (ratio >= 1.0) return 'bg-blue-50 border-blue-200';
    if (ratio >= 0.75) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const cashFlow = parseFloat(monthlyRent) - parseFloat(monthlyPayment);

  return (
    <section id="dscr-calculator" className="py-24 bg-gradient-to-br from-white via-emerald-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-full text-sm font-semibold mb-8 shadow-lg">
            <i className="ri-calculator-line mr-2"></i>
            DSCR Calculator
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Calculate Your DSCR Ratio
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Use our calculator to determine if your investment property qualifies for DSCR financing. A ratio of 1.25 or higher is typically required.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              
              {/* Input Section */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="monthlyRent" className="block text-sm font-semibold text-slate-700 mb-3">
                    Monthly Rental Income
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium text-lg">$</span>
                    <input
                      type="number"
                      id="monthlyRent"
                      value={monthlyRent}
                      onChange={(e) => setMonthlyRent(e.target.value)}
                      className="w-full pl-10 pr-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg font-medium transition-all duration-300"
                      placeholder="3,000"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="monthlyPayment" className="block text-sm font-semibold text-slate-700 mb-3">
                    Monthly Mortgage Payment
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium text-lg">$</span>
                    <input
                      type="number"
                      id="monthlyPayment"
                      value={monthlyPayment}
                      onChange={(e) => setMonthlyPayment(e.target.value)}
                      className="w-full pl-10 pr-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg font-medium transition-all duration-300"
                      placeholder="2,400"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Principal + Interest + Taxes + Insurance</p>
                </div>

                <Button
                  size="lg"
                  className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white font-bold py-4 shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  onClick={calculateDSCR}
                >
                  <i className="ri-calculator-line mr-2"></i>
                  Calculate DSCR
                </Button>
              </div>

              {/* Results Section */}
              <div className="flex flex-col justify-center space-y-6">
                {/* DSCR Result */}
                <div className={`rounded-2xl p-6 border-2 ${getStatusBgColor()} transition-all duration-300`}>
                  <div className="text-center">
                    <div className="text-sm font-medium text-slate-600 mb-2">Your DSCR Ratio</div>
                    <div className={`text-6xl font-bold mb-2 ${getStatusColor()}`}>
                      {dscr}
                    </div>
                    <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor()} bg-white/50`}>
                      {status}
                    </div>
                  </div>
                </div>

                {/* Cash Flow */}
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-slate-600">Monthly Cash Flow</span>
                    <span className={`text-2xl font-bold ${cashFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${Math.abs(cashFlow).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Income: ${parseFloat(monthlyRent).toLocaleString()}</span>
                    <span>Payment: ${parseFloat(monthlyPayment).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Calculator CTA */}
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-slate-100 to-slate-50 rounded-2xl p-12 overflow-hidden border border-slate-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calculator-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">
                Got more property information from Zillow?
                <span className="block mt-2">Try our Advanced DSCR Calculator.</span>
              </h3>
              <p className="text-slate-600 mb-8 text-lg max-w-2xl mx-auto">
                Access our advanced calculator taking into consideration critical criteria in DSCR loan computation.
              </p>
              <Button 
                size="lg" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xl shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calculator-fill mr-2"></i>
                GDL Advanced DSCR Calculator
              </Button>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* DSCR Requirements */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-information-line text-xl text-emerald-600"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-900">DSCR Requirements</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3"></div>
                <span className="text-sm text-slate-600"><span className="font-semibold">1.25+</span> - Excellent (Best rates)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-sm text-slate-600"><span className="font-semibold">1.0-1.24</span> - Good (Qualifies)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
                <span className="text-sm text-slate-600"><span className="font-semibold">0.75-0.99</span> - Fair (May qualify)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                <span className="text-sm text-slate-600"><span className="font-semibold">&lt;0.75</span> - Poor (Unlikely)</span>
              </div>
            </div>
          </div>

          {/* Formula Card */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-function-line text-xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-900">How It's Calculated</h4>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 mb-3">
              <div className="text-center">
                <div className="text-sm text-slate-600 mb-2">DSCR Formula</div>
                <div className="text-lg font-bold text-slate-900">
                  Monthly Rent ÷ Monthly Payment
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              The ratio shows how much rental income covers your mortgage payment. Higher is better!
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
